// v10/src/utils/wbCalculator.js
//
// Cálculo de Weight & Balance en frontend. Port LITERAL del backend
// condor360-backend/services/wbService.js. Se usa para simulación en vivo
// en WBCalculator.js sin ir al backend (latencia 0ms).
//
// IMPORTANTE: si modificás la lógica acá, ACTUALIZÁ también
// condor360-backend/services/wbService.js — el backend re-ejecuta el cálculo
// al guardar (POST /api/wb/save) y NO confía en el resultado del frontend.
//
// Unidades:
//   - Pesos: libras (lb)
//   - Brazos: pulgadas (in)
//   - Combustible (entrada): litros (L)
//   - Combustible para cálculo: galones US (1 gal = 3.7854 L)
//
// Conversiones:
//   - 1 kg = 2.20462 lb
//   - AVGAS densidad: 6 lb/gal US (típico)

export const KG_TO_LB = 2.20462;
export const L_TO_GAL = 0.264172;
export const AVGAS_LB_PER_GAL = 6.0;

/**
 * Calcula peso total, momento, CG y verifica envelope.
 *
 * @param {Object} aircraftData - Datos W&B de la aeronave (de aircraft_models)
 *   @prop {number} empty_weight_lb
 *   @prop {number} empty_weight_arm_in
 *   @prop {number} max_weight_kg
 *   @prop {Object} wb_arms - { pilot_in, passengers_rear_in, fuel_in, baggage_in, oil_in }
 *   @prop {Array<{weight_lb, cg_min_in, cg_max_in}>} cg_envelope_points
 *   @prop {boolean} has_rear_seats
 *
 * @param {Object} inputs - Datos del vuelo
 *   @prop {number} pilot_weight_lb     - Peso del piloto en lb
 *   @prop {number} copilot_weight_lb   - Peso del copiloto en lb (0 si no hay)
 *   @prop {number} passengers_rear_weight_lb - Peso pasajeros traseros en lb (0 si no aplica)
 *   @prop {number} fuel_liters         - Combustible cargado en LITROS
 *   @prop {number} baggage_weight_lb   - Peso equipaje en lb (0 si nada)
 *
 * @returns {Object} resultado con totales, status del envelope y breakdown.
 */
export function calculateWeightBalance(aircraftData, inputs) {
  const breakdown = [];

  // 1. Empty weight
  const emptyWeightLb = Number(aircraftData.empty_weight_lb) || 0;
  const emptyArmIn = Number(aircraftData.empty_weight_arm_in) || 0;

  breakdown.push({
    label: 'Peso vacío',
    weight_lb: emptyWeightLb,
    arm_in: emptyArmIn,
    moment_lb_in: emptyWeightLb * emptyArmIn,
  });

  // 2. Equipment: NO se suma aquí.
  // El empty_weight certificado del POH YA incluye el equipment instalado
  // (hélice, motor, instrumentos, radios, batería, etc.). Sumar los items
  // de aircraft_equipment (R6) sería contarlos dos veces.
  // El admin ve el equipment como referencia documental en el tab
  // "Equipamiento" de la aeronave, no como input de cálculo W&B.
  const envelopePoints = aircraftData.cg_envelope_points || [];

  // 3. Combustible
  const fuelLiters = Number(inputs.fuel_liters) || 0;
  const fuelGal = fuelLiters * L_TO_GAL;
  const fuelLb = fuelGal * AVGAS_LB_PER_GAL;
  const fuelArmIn = Number(aircraftData.wb_arms?.fuel_in) || 0;

  if (fuelLb > 0) {
    breakdown.push({
      label: `Combustible (${fuelLiters.toFixed(1)} L · ${fuelGal.toFixed(1)} gal)`,
      weight_lb: fuelLb,
      arm_in: fuelArmIn,
      moment_lb_in: fuelLb * fuelArmIn,
    });
  }

  // 4. Piloto + copiloto
  const pilotLb = Number(inputs.pilot_weight_lb) || 0;
  const copilotLb = Number(inputs.copilot_weight_lb) || 0;
  const frontArmIn = Number(aircraftData.wb_arms?.pilot_in) || 0;
  const frontWeightLb = pilotLb + copilotLb;

  if (frontWeightLb > 0) {
    breakdown.push({
      label: copilotLb > 0 ? 'Piloto + copiloto' : 'Piloto',
      weight_lb: frontWeightLb,
      arm_in: frontArmIn,
      moment_lb_in: frontWeightLb * frontArmIn,
    });
  }

  // 5. Pasajeros traseros (solo si tiene rear seats)
  const rearPaxLb = Number(inputs.passengers_rear_weight_lb) || 0;
  if (aircraftData.has_rear_seats && rearPaxLb > 0) {
    const rearArmIn = Number(aircraftData.wb_arms?.passengers_rear_in) || 0;
    breakdown.push({
      label: 'Pasajeros traseros',
      weight_lb: rearPaxLb,
      arm_in: rearArmIn,
      moment_lb_in: rearPaxLb * rearArmIn,
    });
  }

  // 6. Equipaje
  const baggageLb = Number(inputs.baggage_weight_lb) || 0;
  if (baggageLb > 0) {
    const baggageArmIn = Number(aircraftData.wb_arms?.baggage_in) || 0;
    breakdown.push({
      label: 'Equipaje',
      weight_lb: baggageLb,
      arm_in: baggageArmIn,
      moment_lb_in: baggageLb * baggageArmIn,
    });
  }

  // 7. Totales
  const totalWeightLb = breakdown.reduce((s, b) => s + b.weight_lb, 0);
  const totalMomentLbIn = breakdown.reduce((s, b) => s + b.moment_lb_in, 0);
  const cgIn = totalWeightLb > 0 ? totalMomentLbIn / totalWeightLb : 0;

  // 8. MTOW (de aircraft_models.max_weight_kg convertido)
  const mtowKg = Number(aircraftData.max_weight_kg) || 0;
  const mtowLb = mtowKg * KG_TO_LB;

  // 9. Validar envelope
  const limits = interpolateEnvelopeLimits(totalWeightLb, envelopePoints);

  let envelopeStatus = 'within';
  let withinEnvelope = true;

  if (mtowLb > 0 && totalWeightLb > mtowLb) {
    envelopeStatus = 'over_mtow';
    withinEnvelope = false;
  } else if (limits && cgIn < limits.cg_min_in) {
    envelopeStatus = 'cg_too_forward';
    withinEnvelope = false;
  } else if (limits && cgIn > limits.cg_max_in) {
    envelopeStatus = 'cg_too_aft';
    withinEnvelope = false;
  }

  return {
    total_weight_lb: round2(totalWeightLb),
    total_moment_lb_in: round2(totalMomentLbIn),
    cg_in: round2(cgIn),
    mtow_lb: round2(mtowLb),
    fuel_used_gal: round2(fuelGal),
    fuel_used_lb: round2(fuelLb),
    within_envelope: withinEnvelope,
    envelope_status: envelopeStatus,
    limits: limits ? { cg_min_in: round2(limits.cg_min_in), cg_max_in: round2(limits.cg_max_in) } : null,
    breakdown: breakdown.map(b => ({
      label: b.label,
      weight_lb: round2(b.weight_lb),
      arm_in: round2(b.arm_in),
      moment_lb_in: round2(b.moment_lb_in),
    })),
  };
}

/**
 * Interpola los límites de CG para un peso dado, usando los puntos del envelope.
 *
 * Si el peso está fuera del rango definido por los puntos, usa el punto más cercano.
 *
 * @param {number} weightLb
 * @param {Array<{weight_lb, cg_min_in, cg_max_in}>} points
 * @returns {{ cg_min_in, cg_max_in } | null}
 */
export function interpolateEnvelopeLimits(weightLb, points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  if (points.length === 1) {
    return {
      cg_min_in: Number(points[0].cg_min_in),
      cg_max_in: Number(points[0].cg_max_in),
    };
  }

  // Normalizar y ordenar por peso ascendente
  const sorted = points
    .map(p => ({
      weight_lb: Number(p.weight_lb),
      cg_min_in: Number(p.cg_min_in),
      cg_max_in: Number(p.cg_max_in),
    }))
    .sort((a, b) => a.weight_lb - b.weight_lb);

  // Bajo el rango
  if (weightLb <= sorted[0].weight_lb) {
    return { cg_min_in: sorted[0].cg_min_in, cg_max_in: sorted[0].cg_max_in };
  }
  // Sobre el rango
  if (weightLb >= sorted[sorted.length - 1].weight_lb) {
    const last = sorted[sorted.length - 1];
    return { cg_min_in: last.cg_min_in, cg_max_in: last.cg_max_in };
  }

  // Interpolación lineal entre dos puntos
  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (weightLb >= lo.weight_lb && weightLb <= hi.weight_lb) {
      const t = (weightLb - lo.weight_lb) / (hi.weight_lb - lo.weight_lb);
      return {
        cg_min_in: lo.cg_min_in + t * (hi.cg_min_in - lo.cg_min_in),
        cg_max_in: lo.cg_max_in + t * (hi.cg_max_in - lo.cg_max_in),
      };
    }
  }
  return null;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}
